import React from "react";
import { observer } from "mobx-react";
import {action, makeObservable, observable} from "mobx";
import { ThemeProvider } from "styled-components";
import { OrbitComponent } from "../base-components/orbit-component/orbit-component";
import {ISpotlightTarget, OnboardingSpotlightComponent} from "../onboarding-tour/spotlight/onboarding-spotlight.component";
import {OnboardingDialogComponent} from "../onboarding-tour/dialog/onboarding-dialog.component";
import {PointDefinition} from "../../services/onboarding-tour/geometry.helper";

@observer
export class OnboardingTour extends OrbitComponent {
    private targetTimeout = null;
    @observable.ref
    private target: ISpotlightTarget | undefined = undefined;
    @observable.ref
    private spotlightOriginPoint: PointDefinition;
    /**
     * When `closing` is set to true it means the routine of closing the onboarding (animation of closing the spotlight, fade out animation, unmount) is in progress.
     */
    @observable.ref
    private closing = false;
    /**
     * `show` is needed because the fade in/out animations. At closing of the onboarding, we need to fade out (show=false) and after that we can unmount (services.onboardingTour.isOpened=false)
     */
    @observable.ref
    private show = true;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    @action
    private closeOnboarding = () => {
        // closing is set to true that means the mr green avatar picture is changed
        this.closing = true;
        // and the spotlight closes (the target is set to undefined)
        this.setTarget(undefined);
        setTimeout(() => {
            // after 400 ms fade out the dialog and overlay
            this.show = false;
        }, 400);
        setTimeout(() => {
            // after 800 ms we can unmount the onboarding (the fade out animation takes 300 ms)
            this.services.onboardingTour.close();
            this.closing = false;
        }, 800);
    }

    @action
    private setSpotlightOriginPoint(o: PointDefinition | undefined) {
        this.spotlightOriginPoint = o || {
            x: Math.round(window.innerWidth / 2),
            y: Math.round(window.innerHeight * (2/3))
        };
    }
    private onLogoCoordinatesUpdate = (c: PointDefinition | undefined) => {
        this.setSpotlightOriginPoint(c);
    };

    @action
    private nextStep = () => {
        // before calling services.onboardingTour.nextStep() i want to cleanup any target spotlight timeouts
        if (this.targetTimeout) {
            clearTimeout(this.targetTimeout);
            this.targetTimeout = null;
        }
        this.services.analytics.sendOrbitUserFlowEvent("Onboarding", "Click_Next", (this.services.onboardingTour.stepIndex + 1).toString());
        // call next step and get the index
        let newStepIndex = this.services.onboardingTour.nextStep();
        // get the new step
        let step = this.services.onboardingTour.steps[newStepIndex];
        this.setTargetAndScheduleNextOfTheSameStep(step.targets, 0);
    };

    private setTargetAndScheduleNextOfTheSameStep(allTargets: ISpotlightTarget[], index: number) {
        if (allTargets.length > index && allTargets[index]) {
            this.setTarget(allTargets[index]);
        }
        if (allTargets.length > index + 1) {
            this.targetTimeout = setTimeout(() => {
                this.setTargetAndScheduleNextOfTheSameStep(allTargets, index+1);
            }, 400);
        }
    }

    @action
    private setTarget(t: ISpotlightTarget | undefined) {
        this.target = t;
    }

    render() {
        if (!this.services.onboardingTour.isOpened) {
            return null;
        }
        if (!this.services.onboardingTour.allClearToDisplayOnboarding) {
            return null;
        }
        let stepIndex = this.services.onboardingTour.stepIndex;
        let step = this.services.onboardingTour.steps[stepIndex];
        return (<ThemeProvider theme={this.services.theme.mainContentTheme}>
            <OnboardingSpotlightComponent zIndex={1000} originPoint={this.spotlightOriginPoint} target={this.target} show={this.show} />
            <OnboardingDialogComponent
                zIndex={1000}
                onLogoCoordinatesUpdate={this.onLogoCoordinatesUpdate}
                onNext={this.nextStep}
                onDone={this.closeOnboarding}
                onClose={this.closeOnboarding}
                step={step}
                stepIndex={stepIndex}
                stepsTotal={this.services.onboardingTour.steps.length}
                closing={this.closing}
                show={this.show}
            />
        </ThemeProvider>);
    }
}